// <s-scratch-card> paints a scratchable foil over its [data-scratch-prize] region.
// Shoppers rub the foil away with a pointer; once enough is cleared the foil fades out.
// Keyboard and assistive-tech users reveal it with the [data-scratch-reveal] button.
// The revealed state is remembered for the browser session so returning shoppers see the prize.
const STORAGE_KEY = "petnest-scratch-revealed";
const REVEAL_RATIO = 0.45;

class ScratchCard extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #drawing = false;
  #last = null;
  #moves = 0;
  #resizeObserver = null;

  connectedCallback() {
    this.#canvas = this.querySelector("canvas[data-scratch-surface]");
    if (!this.#canvas) return;
    this.#ctx = this.#canvas.getContext("2d", { willReadFrequently: true });

    let revealed = false;
    try {
      revealed = window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch (e) {}
    if (revealed) {
      this.#finish(false);
      return;
    }

    this.#paint();
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.hasAttribute("revealed") && this.#moves === 0) this.#paint();
    });
    this.#resizeObserver.observe(this);

    // Stop the browser from panning, zooming or bouncing the page while a finger is on the foil.
    this.#canvas.addEventListener("touchstart", this.#blockTouch, { passive: false });
    this.#canvas.addEventListener("touchmove", this.#blockTouch, { passive: false });
    this.#canvas.addEventListener("pointerdown", this.#onDown);
    this.#canvas.addEventListener("pointermove", this.#onMove);
    this.#canvas.addEventListener("pointerup", this.#onUp);
    this.#canvas.addEventListener("pointercancel", this.#onUp);
    this.#canvas.addEventListener("pointerleave", this.#onUp);
    this.querySelector("[data-scratch-reveal]")?.addEventListener("click", () => this.#finish(true));
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #paint() {
    const rect = this.#canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    this.#canvas.width = Math.max(1, Math.round(rect.width * ratio));
    this.#canvas.height = Math.max(1, Math.round(rect.height * ratio));
    const ctx = this.#ctx;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.globalCompositeOperation = "source-over";

    const gradient = ctx.createLinearGradient(0, 0, rect.width, 0);
    gradient.addColorStop(0, "#d4a843");
    gradient.addColorStop(0.45, "#f2d47e");
    gradient.addColorStop(0.6, "#e9c463");
    gradient.addColorStop(1, "#cf9f38");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Light sparkle texture so the foil reads as scratchable.
    ctx.fillStyle = "rgba(255, 255, 255, 0.35)";
    for (let i = 0; i < Math.round((rect.width * rect.height) / 220); i++) {
      ctx.fillRect(Math.random() * rect.width, Math.random() * rect.height, 1.5, 1.5);
    }
  }

  #point(event) {
    const rect = this.#canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  #blockTouch = (event) => {
    if (event.cancelable) event.preventDefault();
  };

  #onDown = (event) => {
    event.preventDefault();
    this.#drawing = true;
    this.#last = this.#point(event);
    this.#canvas.setPointerCapture?.(event.pointerId);
    this.setAttribute("scratching", "");
    this.#scratch(this.#last, this.#last);
  };

  #onMove = (event) => {
    if (!this.#drawing) return;
    event.preventDefault();
    const point = this.#point(event);
    this.#scratch(this.#last, point);
    this.#last = point;
    this.#moves += 1;
    if (this.#moves % 12 === 0) this.#check();
  };

  #onUp = () => {
    if (!this.#drawing) return;
    this.#drawing = false;
    this.#check();
  };

  #scratch(from, to) {
    const ctx = this.#ctx;
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = 34;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x + 0.01, to.y);
    ctx.stroke();
  }

  #check() {
    const { width, height } = this.#canvas;
    if (!width || !height) return;
    const data = this.#ctx.getImageData(0, 0, width, height).data;
    let cleared = 0;
    let sampled = 0;
    for (let i = 3; i < data.length; i += 4 * 16) {
      sampled += 1;
      if (data[i] === 0) cleared += 1;
    }
    if (cleared / sampled >= REVEAL_RATIO) this.#finish(true);
  }

  #finish(announce) {
    if (this.hasAttribute("revealed")) return;
    this.setAttribute("revealed", "");
    this.removeAttribute("scratching");
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {}
    const prize = this.querySelector("[data-scratch-prize]");
    prize?.removeAttribute("aria-hidden");
    if (announce) {
      prize?.setAttribute("aria-live", "polite");
      // Sound removed at the merchant's request; the reveal is silent and keeps only the visual burst.
      this.#burst();
    }
  }


  // One soft firework: a rounded low "boom" and a sparkle of crackles.
  // Synthesised with Web Audio so no file loads. Browsers only allow sound after a shopper gesture;
  // scratching or pressing the reveal button counts.
  #chime() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    let ctx;
    try {
      ctx = new AudioCtx();
    } catch (e) {
      return;
    }
    const master = ctx.createGain();
    master.gain.value = 0.5;
    master.connect(ctx.destination);
    const now = ctx.currentTime;

    // Boom: soft attack and filtered low so it reads as a distant firework, not a sharp crack.
    const at = now;
    const thump = ctx.createOscillator();
    thump.type = "sine";
    thump.frequency.setValueAtTime(95, at);
    thump.frequency.exponentialRampToValueAtTime(40, at + 0.8);
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0.0001, at);
    thumpGain.gain.exponentialRampToValueAtTime(0.9, at + 0.04);
    thumpGain.gain.exponentialRampToValueAtTime(0.0001, at + 1);
    thump.connect(thumpGain).connect(master);
    thump.start(at);
    thump.stop(at + 1.05);

    const length = Math.floor(ctx.sampleRate * 1.2);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) samples[i] = Math.random() * 2 - 1;
    const rumble = ctx.createBufferSource();
    rumble.buffer = buffer;
    const lowpass = ctx.createBiquadFilter();
    lowpass.type = "lowpass";
    lowpass.frequency.setValueAtTime(700, at);
    lowpass.frequency.exponentialRampToValueAtTime(150, at + 1);
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.setValueAtTime(0.0001, at);
    rumbleGain.gain.exponentialRampToValueAtTime(0.5, at + 0.05);
    rumbleGain.gain.exponentialRampToValueAtTime(0.0001, at + 1.1);
    rumble.connect(lowpass).connect(rumbleGain).connect(master);
    rumble.start(at);
    rumble.stop(at + 1.2);

    // Sparkle: a scatter of tiny quiet crackles as the burst spreads out.
    for (let i = 0; i < 14; i += 1) {
      const t = at + 0.2 + Math.random() * 0.9;
      const crackle = ctx.createOscillator();
      crackle.type = "triangle";
      crackle.frequency.value = 2500 + Math.random() * 2500;
      const crackleGain = ctx.createGain();
      crackleGain.gain.setValueAtTime(0.0001, t);
      crackleGain.gain.exponentialRampToValueAtTime(0.04, t + 0.004);
      crackleGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
      crackle.connect(crackleGain).connect(master);
      crackle.start(t);
      crackle.stop(t + 0.06);
    }

    window.setTimeout(() => ctx.close().catch(() => {}), 3000);
  }

  // One-shot confetti burst from the card's centre, drawn above the page so the card's rounded clip doesn't cut it off.
  #burst() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const rect = this.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;
    const colors = ["#d4a64a", "#f3d27a", "#c8102e", "#1f5a3a", "#ffffff"];
    const layer = document.createElement("div");
    layer.setAttribute("aria-hidden", "true");
    layer.style.cssText = "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden;";
    document.body.appendChild(layer);
    const pieces = 70;
    let done = 0;
    for (let i = 0; i < pieces; i += 1) {
      const piece = document.createElement("span");
      const size = 6 + Math.random() * 6;
      const round = Math.random() < 0.3;
      piece.style.cssText = `position:absolute;left:${originX}px;top:${originY}px;width:${size}px;height:${round ? size : size * 0.45}px;background:${colors[i % colors.length]};border-radius:${round ? "50%" : "1px"};`;
      layer.appendChild(piece);
      const angle = Math.random() * Math.PI * 2;
      const distance = 90 + Math.random() * 190;
      const dx = Math.cos(angle) * distance;
      const dy = Math.sin(angle) * distance - 80;
      const spin = (Math.random() - 0.5) * 1080;
      const duration = 1100 + Math.random() * 700;
      const animation = piece.animate(
        [
          { transform: "translate(-50%, -50%) rotate(0deg) scale(0.6)", opacity: 1 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) rotate(${spin * 0.6}deg) scale(1)`, opacity: 1, offset: 0.45 },
          { transform: `translate(calc(-50% + ${dx * 1.15}px), calc(-50% + ${dy + 220}px)) rotate(${spin}deg) scale(0.9)`, opacity: 0 },
        ],
        { duration, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "forwards" },
      );
      animation.onfinish = () => {
        done += 1;
        if (done === pieces) layer.remove();
      };
    }
  }
}

if (!customElements.get("s-scratch-card")) customElements.define("s-scratch-card", ScratchCard);
